<script lang="ts">
  import Button from '../base/Button.svelte';
  import { Crafts } from '@game/database/CraftDB';
  import { itemsDB } from '@game/database';
  import { animations } from '@utils/animations';
  import { onDestroy } from 'svelte';
  import { audioManager } from '@engine/audio';
  import { profileStore, profileActions } from '@stores/game/profile';
  import { UI_ICON_SVG_PATHS } from '@assets/svg';
  import { getItemIconUrl } from '@/assets/icons';

  interface CraftInfo {
    craftId: string;
    config: any;
    cost: any;
    canCraft: boolean;
    isUnlocked: boolean;
    currentInputAmount: number;
    currentOutputAmount: number;
  }

  let { craft, onCraftStarted = () => {} } = $props<{
    craft: CraftInfo;
    onCraftStarted?: (data: { craftId: string; quantity: number }) => void;
  }>();

  // Подписываемся на изменения в активных крафтах через readable store
  const activeCraftsReadable = Crafts.activeCraftsStore();
  const activeCrafts = $derived($activeCraftsReadable);

  // Проверяем, активен ли этот крафт
  const isCraftActive = $derived.by(() => {
    return activeCrafts.some(activeCraft => activeCraft.craftId === craft.craftId);
  });

  // RAF-стейт для плавного обновления прогресса
  let now = $state(0);

  // Получаем прогресс активного крафта
  const craftProgress = $derived.by(() => {
    if (!isCraftActive) return 0;
    const c = activeCrafts.find(a => a.craftId === craft.craftId);
    if (!c) return 0;
    const current = now || Date.now();
    const total = c.endTime - c.startTime;
    const elapsed = current - c.startTime;
    return Math.min(Math.max(elapsed / total, 0), 1);
  });

  // Получаем оставшееся время
  const remainingTime = $derived.by(() => {
    if (!isCraftActive) return 0;
    const c = activeCrafts.find(a => a.craftId === craft.craftId);
    if (!c) return 0;
    const current = now || Date.now();
    const remaining = c.endTime - current;
    return Math.max(Math.ceil(remaining / 1000), 0);
  });

  // Актуальные значения инвентаря и доступности крафта (реактивно)
  const inputAmount = $derived.by(() => {
    profileStore.get();
    return profileActions.getInventoryItemQuantity(craft.cost.inputItem);
  });

  const outputAmount = $derived.by(() => {
    profileStore.get();
    return profileActions.getInventoryItemQuantity(craft.cost.outputItem);
  });

  const canCraftNow = $derived.by(() => {
    // Завязываемся на профиль, чтобы реагировать на списание/получение ресурсов
    profileStore.get();
    return Crafts.canCraft(craft.craftId, 1);
  });

  let rafId: number | null = null;
  let isButtonPressed = $state(false);

  // Реактивно управляем RAF
  $effect(() => {
    if (isCraftActive) {
      startRAF();
    } else {
      stopRAF();
    }
  });

  onDestroy(() => {
    stopRAF();
  });

  function startRAF() {
    if (rafId) return;
    const loop = () => {
      // Используем абсолютное время, чтобы совпадало с endTime/startTime
      now = Date.now();
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
  }

  function stopRAF() {
    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  }

  function getItemIcon(itemId: string): string {
    const iconPath = itemsDB.getItemIcon(itemId);
    return getItemIconUrl(iconPath);
  }

  // Количества берем из реактивных значений

  function getCraftName(): string {
    // Получаем название выходного предмета из ItemsDB
    return itemsDB.getItemName(craft.cost.outputItem);
  }

  function formatQuantity(quantity: number): string {
    if (quantity >= 1000000) {
      return `${(quantity / 1000000).toFixed(1)}M`;
    } else if (quantity >= 1000) {
      return `${(quantity / 1000).toFixed(1)}K`;
    }
    return quantity.toString();
  }

  function formatTime(seconds: number): string {
    if (seconds >= 3600) {
      const hours = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${mins}m`;
    } else if (seconds >= 60) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}m ${secs}s`;
    }
    return `${seconds}s`;
  }

  function handleCraftClick() {
    console.log('🔨 Debug: Attempting to start craft:', craft.craftId);
    console.log('🔨 Debug: Can craft now:', canCraftNow);
    console.log('🔨 Debug: Current input amount:', inputAmount);
    console.log('🔨 Debug: Required input:', craft.cost.inputQuantity);

    // Визуальная обратная связь
    isButtonPressed = true;
    setTimeout(() => {
      isButtonPressed = false;
    }, 150);

    if (!canCraftNow) return;
    const success = Crafts.startCraft(craft.craftId, 1);
    if (success) {
      console.log('✅ Debug: Craft started successfully');
      onCraftStarted({ craftId: craft.craftId, quantity: 1 });
      audioManager.playSFX('ui_click', { volume: 0.7 });
    } else {
      console.log('❌ Debug: Craft start failed');
    }
  }

  function animateCraftButton(event: Event) {
    const button = event.currentTarget as HTMLElement;
    if (button) {
      animations.buttonPress(button);
    }
  }
</script>

<div
  class="craft-item"
  class:craft-item--disabled={!craft.canCraft}
  class:craft-item--active={isCraftActive}
>
  <!-- Заголовок крафта -->
  <div class="craft-header">
    <h3 class="craft-title">{getCraftName()}</h3>
  </div>

  <!-- Основное содержимое -->
  <div class="craft-content">
    <!-- Левая часть: мини-иконки с количествами -->
    <div class="craft-inventory">
      <div class="inventory-item">
        <img src={getItemIcon(craft.cost.inputItem)} alt="Input" class="mini-icon" />
        <span class="inventory-amount">{formatQuantity(inputAmount)}</span>
      </div>
      <div class="inventory-item">
        <img src={getItemIcon(craft.cost.outputItem)} alt="Output" class="mini-icon" />
        <span class="inventory-amount">{formatQuantity(outputAmount)}</span>
      </div>
    </div>

    <!-- Центральная часть: большие иконки с рецептом -->
    <div class="craft-recipe">
      <div class="recipe-input">
        <img src={getItemIcon(craft.cost.inputItem)} alt="Input" class="large-icon" />
        <span class="recipe-quantity">{formatQuantity(craft.cost.inputQuantity)}</span>
      </div>
      <div class="recipe-output">
        <img src={getItemIcon(craft.cost.outputItem)} alt="Output" class="large-icon" />
        <span class="recipe-quantity">{formatQuantity(craft.cost.outputQuantity)}</span>
      </div>
      <!-- Стрелка над иконками -->
      <div class="recipe-arrow">
        <img src={UI_ICON_SVG_PATHS.arrow_right} alt="Arrow" class="arrow-icon" />
      </div>
    </div>

    <!-- Правая часть: время и кнопка/прогресс -->
    <div class="craft-actions">
      {#if isCraftActive}
        <!-- Состояние активного крафта -->
        <div class="craft-progress-container">
          <div class="progress-bar">
            <div class="progress-fill" style="width: {craftProgress * 100}%"></div>
          </div>
          <div class="progress-time">
            {formatTime(remainingTime)}
          </div>
        </div>
      {:else}
        <div
          class={isButtonPressed ? 'button-pressed' : ''}
          class:craft-button-disabled={!canCraftNow}
        >
          <Button
            variant={canCraftNow ? 'success' : 'primary'}
            disabled={!canCraftNow}
            size="md"
            on:click={e => {
              animateCraftButton(e);
              handleCraftClick();
            }}
          >
            Craft
          </Button>
        </div>
        <!-- Обычное состояние -->
        {#if craft.cost.timeSeconds > 0}
          <div class="craft-time">
            {formatTime(craft.cost.timeSeconds)}
          </div>
        {/if}
      {/if}
    </div>
  </div>
</div>

<style lang="postcss">
  @reference "@/styles/theme.css";

  .craft-item {
    @apply bg-dark-bg/50;
    @apply border border-neon-green/20;
    @apply rounded-lg p-4;
    @apply transition-all duration-200;
    @apply hover:border-neon-green/40;
    @apply hover:bg-dark-bg/70;
    @apply flex flex-col gap-4;
  }

  .craft-item--disabled {
    @apply border-gray-600/30;
  }

  .craft-item--disabled:hover {
    @apply border-gray-600/30;
    @apply transform-none;
  }

  .craft-item--active {
    @apply border-neon-orange/50;
    @apply bg-neon-orange/5;
    @apply shadow-lg shadow-neon-orange/20;
  }

  .craft-header {
    @apply text-center;
    @apply border-b border-neon-green/20 pb-2;
  }

  .craft-title {
    @apply text-lg font-bold text-neon-cyan;
    @apply font-sci-fi;
    @apply m-0;
    text-shadow: 0 0 8px var(--color-neon-cyan);
  }

  .craft-content {
    @apply flex items-center gap-4;
  }

  .craft-inventory {
    @apply flex flex-col gap-2;
    @apply min-w-12;
  }

  .inventory-item {
    @apply flex items-center gap-2;
    @apply bg-dark-bg/30;
    @apply px-3 py-2;
    @apply rounded;
    @apply min-w-20;
  }

  .mini-icon {
    @apply w-6 h-6 object-contain;
  }

  .inventory-amount {
    @apply text-xs font-mono;
    @apply text-neon-green;
    @apply font-bold;
  }

  .craft-recipe {
    @apply flex items-center gap-4;
    @apply flex-1;
    @apply justify-center;
    @apply relative;
  }

  .recipe-input,
  .recipe-output {
    @apply flex flex-col items-center gap-1;
  }

  .large-icon {
    @apply w-14 h-14 object-contain;
  }

  .recipe-quantity {
    @apply text-sm font-mono;
    @apply text-neon-orange;
    @apply font-bold;
    @apply bg-neon-orange/10;
    @apply px-2 py-1;
    @apply rounded;
  }

  .recipe-arrow {
    @apply absolute;
    @apply top-1/3;
    @apply left-1/2;
    @apply transform -translate-x-1/2 -translate-y-1/2;
    @apply z-10;
  }

  .arrow-icon {
    @apply w-10 h-10;
    @apply drop-shadow-lg;
    filter: drop-shadow(0 0 8px var(--color-neon-cyan));
    color: var(--color-neon-cyan);
  }

  .craft-actions {
    @apply flex flex-col items-center gap-2;
  }

  .craft-time {
    @apply text-xs text-neon-orange;
    @apply font-mono;
    @apply bg-neon-orange/10 px-3 py-2 rounded;
    @apply font-bold;
  }

  .craft-progress-container {
    @apply w-full;
  }

  .progress-bar {
    @apply w-full h-2;
    @apply bg-gray-700 rounded;
    @apply overflow-hidden;
  }

  .progress-fill {
    @apply h-full;
    @apply bg-gradient-to-r from-neon-orange to-neon-yellow;
    @apply transition-all duration-300;
  }

  .progress-time {
    @apply text-xs text-neon-orange mt-1;
    @apply text-center;
    @apply font-mono;
  }

  .button-pressed {
    @apply transform scale-95;
    @apply transition-transform duration-150;
  }

  .craft-button-disabled {
    @apply opacity-50;
    @apply cursor-not-allowed;
    @apply pointer-events-none;
  }
</style>
